import React, { useState, useCallback } from 'react';
import OAuthConnectButton from './OAuthConnectButton';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AdsAccount {
  customerId: string;
  descriptiveName: string;
  currencyCode?: string;
  timeZone?: string;
}

interface CampaignRow {
  name: string;
  status: string;
  type: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
}

interface KeywordRow {
  keyword: string;
  matchType: string;
  qualityScore: number | null;
  campaign: string;
  clicks: number;
  cost: number;
  conversions: number;
}

interface AuditSummary {
  totalSpend: number;
  totalClicks: number;
  totalConversions: number;
  cpa: number;
  campaignCount: number;
  keywordCount: number;
  wastedSpendKeywords: number;
}

interface AuditResponse {
  summary: AuditSummary;
  analysis: string;
  campaigns: CampaignRow[];
  keywords: KeywordRow[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

function formatCurrency(value: number): string {
  return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

function formatPct(value: number): string {
  return (value * 100).toFixed(2) + '%';
}

/**
 * Minimal markdown-to-HTML renderer.
 * Handles: ## headings, **bold**, *italic*, - bullets, numbered lists, `code`, ```code blocks```
 */
function renderMarkdown(md: string): string {
  // Escape HTML entities
  let html = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Fenced code blocks
  html = html.replace(/```[\s\S]*?```/g, (match) => {
    const inner = match.slice(3, -3).replace(/^\w*\n/, '');
    return `<pre class="bg-gray-800 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm my-3"><code>${inner}</code></pre>`;
  });

  // Split into lines for block-level processing
  const lines = html.split('\n');
  const output: string[] = [];
  let inList = false;
  let listType: 'ul' | 'ol' | null = null;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Skip lines inside <pre> blocks (already handled)
    if (line.includes('<pre')) {
      // Find closing </pre> — pass through as-is
      output.push(line);
      continue;
    }

    // Headings
    if (/^####\s/.test(line)) {
      closeList();
      line = `<h4 class="text-base font-semibold text-gray-800 mt-4 mb-2">${line.replace(/^####\s/, '')}</h4>`;
      output.push(line);
      continue;
    }
    if (/^###\s/.test(line)) {
      closeList();
      line = `<h3 class="text-lg font-semibold text-gray-800 mt-5 mb-2">${line.replace(/^###\s/, '')}</h3>`;
      output.push(line);
      continue;
    }
    if (/^##\s/.test(line)) {
      closeList();
      line = `<h2 class="text-xl font-bold text-gray-900 mt-6 mb-3">${line.replace(/^##\s/, '')}</h2>`;
      output.push(line);
      continue;
    }
    if (/^#\s/.test(line)) {
      closeList();
      line = `<h1 class="text-2xl font-bold text-gray-900 mt-6 mb-3">${line.replace(/^#\s/, '')}</h1>`;
      output.push(line);
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s/.test(line)) {
      if (listType !== 'ul') {
        closeList();
        output.push('<ul class="list-disc list-inside space-y-1 my-2 text-gray-700">');
        inList = true;
        listType = 'ul';
      }
      line = `<li>${applyInline(line.replace(/^\s*[-*]\s/, ''))}</li>`;
      output.push(line);
      continue;
    }

    // Ordered list
    if (/^\s*\d+[.)]\s/.test(line)) {
      if (listType !== 'ol') {
        closeList();
        output.push('<ol class="list-decimal list-inside space-y-1 my-2 text-gray-700">');
        inList = true;
        listType = 'ol';
      }
      line = `<li>${applyInline(line.replace(/^\s*\d+[.)]\s/, ''))}</li>`;
      output.push(line);
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      closeList();
      output.push('<br/>');
      continue;
    }

    // Regular paragraph
    closeList();
    output.push(`<p class="text-gray-700 my-1">${applyInline(line)}</p>`);
  }

  closeList();
  return output.join('\n');

  function closeList() {
    if (inList && listType) {
      output.push(listType === 'ul' ? '</ul>' : '</ol>');
      inList = false;
      listType = null;
    }
  }

  function applyInline(text: string): string {
    // Bold
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');
    // Italic
    text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Inline code
    text = text.replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-pink-600 px-1 py-0.5 rounded text-sm">$1</code>');
    return text;
  }
}

// ---------------------------------------------------------------------------
// Spinner SVG (reused)
// ---------------------------------------------------------------------------

const Spinner: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GoogleAdsAudit: React.FC = () => {
  // Connection state
  const [connected, setConnected] = useState(false);

  // Accounts
  const [accounts, setAccounts] = useState<AdsAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  // Audit form
  const [websiteContext, setWebsiteContext] = useState('');

  // Audit state
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [result, setResult] = useState<AuditResponse | null>(null);

  // Accordion toggles
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKeywords, setShowKeywords] = useState(false);

  // Fetch accounts when connected
  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    setAccountsError(null);
    setAccounts([]);
    setSelectedCustomerId('');
    try {
      const res = await fetch('/api/seotools/google-ads/accounts', { headers: authHeaders() });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || body?.error?.message || `Failed to load accounts (${res.status})`);
      }
      const json = await res.json();
      const data: AdsAccount[] = (json.accounts || json || []).map((a: any) => ({
        customerId: a.customerId || a.id,
        descriptiveName: a.descriptiveName || a.name,
        currencyCode: a.currencyCode || a.currency,
        timeZone: a.timeZone,
      }));
      setAccounts(data);
      if (data.length === 1) {
        setSelectedCustomerId(data[0].customerId);
      }
    } catch (err: unknown) {
      setAccountsError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  const handleConnect = useCallback(() => {
    setConnected(true);
    loadAccounts();
  }, [loadAccounts]);

  const handleDisconnect = useCallback(() => {
    setConnected(false);
    setAccounts([]);
    setSelectedCustomerId('');
    setResult(null);
  }, []);

  // Run audit
  const handleRunAudit = async () => {
    if (!selectedCustomerId) return;

    setAuditLoading(true);
    setAuditError(null);
    setResult(null);

    try {
      const res = await fetch('/api/seotools/google-ads/audit', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          customerId: selectedCustomerId,
          websiteContext: websiteContext.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || body?.error?.message || `Audit failed (${res.status})`);
      }

      const raw = await res.json();
      // Map backend response to frontend interface
      const data: AuditResponse = {
        analysis: raw.analysis,
        summary: {
          totalSpend: parseFloat(raw.summary?.totalSpend ?? 0),
          totalClicks: raw.summary?.totalClicks ?? 0,
          totalConversions: parseFloat(raw.summary?.totalConversions ?? 0),
          cpa: raw.summary?.cpa === 'N/A' ? 0 : parseFloat(raw.summary?.cpa ?? 0),
          campaignCount: raw.summary?.campaignCount ?? 0,
          keywordCount: raw.summary?.keywordCount ?? 0,
          wastedSpendKeywords: raw.summary?.wastedCount ?? raw.summary?.wastedSpendKeywords ?? 0,
        },
        campaigns: (raw.rawData?.campaigns ?? raw.campaigns ?? []).map((c: any) => ({
          name: c.name, status: c.status, type: c.type,
          impressions: c.impressions ?? 0, clicks: c.clicks ?? 0,
          cost: parseFloat(c.cost ?? 0), conversions: parseFloat(c.conversions ?? 0),
          ctr: c.ctr ? parseFloat(c.ctr) : 0,
        })),
        keywords: (raw.rawData?.keywords ?? raw.keywords ?? []).map((k: any) => ({
          keyword: k.keyword, matchType: k.matchType,
          qualityScore: k.qualityScore, campaign: k.campaign,
          clicks: k.clicks ?? 0, cost: parseFloat(k.cost ?? 0),
          conversions: parseFloat(k.conversions ?? 0),
        })),
      };
      setResult(data);
    } catch (err: unknown) {
      setAuditError(err instanceof Error ? err.message : 'Audit failed');
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Google Ads Audit</h2>
        <p className="text-sm text-gray-500 mt-1">
          Connect your Google Ads account to get an AI-powered audit with actionable recommendations.
        </p>
      </div>

      {/* OAuth Connection */}
      <OAuthConnectButton
        service="google_ads"
        label="Google Ads"
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
      />

      {/* Account Selection & Audit Form */}
      {connected && (
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200 space-y-4">
          {/* Account selector */}
          <div>
            <label htmlFor="ads-account" className="block text-sm font-medium text-gray-700 mb-1">
              Google Ads Account <span className="text-red-500">*</span>
            </label>
            {accountsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner /> Loading accounts...
              </div>
            ) : accountsError ? (
              <div className="text-sm text-red-600">
                {accountsError}
                <button
                  onClick={loadAccounts}
                  className="ml-2 text-blue-600 hover:underline text-xs"
                >
                  Retry
                </button>
              </div>
            ) : accounts.length === 0 ? (
              <p className="text-sm text-gray-500">No accounts found. Make sure your Google Ads account is linked.</p>
            ) : (
              <select
                id="ads-account"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                disabled={auditLoading}
              >
                <option value="">Select an account...</option>
                {accounts.map((acc) => (
                  <option key={acc.customerId} value={acc.customerId}>
                    {acc.descriptiveName || acc.customerId} ({acc.customerId})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Website context (optional) */}
          <div>
            <label htmlFor="ads-context" className="block text-sm font-medium text-gray-700 mb-1">
              Website / Business Context <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              id="ads-context"
              value={websiteContext}
              onChange={(e) => setWebsiteContext(e.target.value)}
              placeholder="e.g. We are a plumbing company in Austin, TX. Our main services are emergency plumbing, drain cleaning, and water heater installation. Our target CPA is $50."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={auditLoading}
            />
            <p className="text-xs text-gray-400 mt-1">
              Providing context helps the AI give more relevant recommendations.
            </p>
          </div>

          {/* Run Audit button */}
          <div className="flex justify-end">
            <button
              onClick={handleRunAudit}
              disabled={auditLoading || !selectedCustomerId}
              className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {auditLoading ? (
                <span className="flex items-center gap-2">
                  <Spinner />
                  Running Audit...
                </span>
              ) : (
                'Run Audit'
              )}
            </button>
          </div>

          {auditLoading && (
            <p className="text-xs text-gray-500 text-center">
              This may take 30+ seconds while we pull your data and run the AI analysis.
            </p>
          )}
        </div>
      )}

      {/* Error */}
      {auditError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <strong>Error:</strong> {auditError}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Audit Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <SummaryCard label="Total Spend" value={formatCurrency(result.summary.totalSpend)} color="blue" />
              <SummaryCard label="Total Clicks" value={formatNumber(result.summary.totalClicks)} color="purple" />
              <SummaryCard label="Total Conversions" value={formatNumber(result.summary.totalConversions)} color="green" />
              <SummaryCard label="CPA" value={formatCurrency(result.summary.cpa)} color="orange" />
              <SummaryCard label="Campaigns" value={formatNumber(result.summary.campaignCount)} color="indigo" />
              <SummaryCard label="Keywords" value={formatNumber(result.summary.keywordCount)} color="teal" />
              <SummaryCard
                label="Wasted Spend Keywords"
                value={formatNumber(result.summary.wastedSpendKeywords)}
                color="red"
              />
            </div>
          </div>

          {/* AI Analysis */}
          {result.analysis && (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                  </svg>
                  AI Analysis
                </h3>
              </div>
              <div
                className="p-6 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(result.analysis) }}
              />
            </div>
          )}

          {/* Raw Data: Campaigns */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowCampaigns((v) => !v)}
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-base font-semibold text-gray-800">
                Campaigns ({result.campaigns.length})
              </h3>
              <svg
                className={`h-5 w-5 text-gray-400 transition-transform ${showCampaigns ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
            {showCampaigns && (
              <div className="border-t border-gray-200 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 font-medium text-gray-600">Campaign</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Type</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-right">Impressions</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-right">Clicks</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-right">Cost</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-right">Conversions</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-right">CTR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.campaigns.map((c, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="px-4 py-2 text-gray-800 font-medium max-w-[220px] truncate" title={c.name}>
                          {c.name}
                        </td>
                        <td className="px-4 py-2">
                          <StatusBadge status={c.status} />
                        </td>
                        <td className="px-4 py-2 text-gray-600">{c.type}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{formatNumber(c.impressions)}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{formatNumber(c.clicks)}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{formatCurrency(c.cost)}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{formatNumber(c.conversions)}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{formatPct(c.ctr)}</td>
                      </tr>
                    ))}
                    {result.campaigns.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                          No campaign data available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Raw Data: Keywords */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowKeywords((v) => !v)}
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-base font-semibold text-gray-800">
                Keywords ({result.keywords.length})
              </h3>
              <svg
                className={`h-5 w-5 text-gray-400 transition-transform ${showKeywords ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
            {showKeywords && (
              <div className="border-t border-gray-200 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 font-medium text-gray-600">Keyword</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Match Type</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-center">Quality Score</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Campaign</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-right">Clicks</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-right">Cost</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-right">Conversions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.keywords.map((kw, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="px-4 py-2 text-gray-800 font-medium max-w-[200px] truncate" title={kw.keyword}>
                          {kw.keyword}
                        </td>
                        <td className="px-4 py-2 text-gray-600">{kw.matchType}</td>
                        <td className="px-4 py-2 text-center">
                          {kw.qualityScore != null ? (
                            <span
                              className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                                kw.qualityScore >= 7
                                  ? 'bg-green-100 text-green-700'
                                  : kw.qualityScore >= 4
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {kw.qualityScore}/10
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-600 max-w-[180px] truncate" title={kw.campaign}>
                          {kw.campaign}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">{formatNumber(kw.clicks)}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{formatCurrency(kw.cost)}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{formatNumber(kw.conversions)}</td>
                      </tr>
                    ))}
                    {result.keywords.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                          No keyword data available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const colorMap: Record<string, { bg: string; text: string; value: string }> = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-500', value: 'text-blue-700' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-500', value: 'text-purple-700' },
  green: { bg: 'bg-green-50', text: 'text-green-500', value: 'text-green-700' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-500', value: 'text-orange-700' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-500', value: 'text-indigo-700' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-500', value: 'text-teal-700' },
  red: { bg: 'bg-red-50', text: 'text-red-500', value: 'text-red-700' },
};

const SummaryCard: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => {
  const c = colorMap[color] || colorMap.blue;
  return (
    <div className={`${c.bg} rounded-lg p-4 text-center`}>
      <div className={`text-2xl font-bold ${c.value}`}>{value}</div>
      <div className={`text-xs ${c.text} mt-1`}>{label}</div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = status.toLowerCase();
  const styles =
    s === 'enabled' || s === 'active'
      ? 'bg-green-100 text-green-700 border-green-200'
      : s === 'paused'
      ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
      : 'bg-gray-100 text-gray-600 border-gray-200';

  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${styles}`}>
      {status}
    </span>
  );
};

export default GoogleAdsAudit;
